import React, { useState, useEffect, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Building2, ChevronRight, AlertTriangle, AlertOctagon, ArrowUpRight, Mail, Search, Pencil, Plus, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import sponsorService from '../../services/sponsorService';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

const SponsorSelector = () => {
    const [sponsors, setSponsors] = useState([]);             // sponsors user is assigned to
    const [allSponsors, setAllSponsors] = useState([]);       // all sponsors in system (superuser only)

    const [loading, setLoading] = useState(true);
    const [selectedSponsor, setSelectedSponsor] = useState(null);
    const [fetchError, setFetchError] = useState(false);
    const [autoSelecting, setAutoSelecting] = useState(false);
    const [retryToken, setRetryToken] = useState(0);

    // Search over the assigned-sponsor cards, offered only when the list is
    // long enough to need it (FECOA-1783)
    const [sponsorSearch, setSponsorSearch] = useState('');
    const showSponsorSearch = sponsors.length > 6;
    const visibleSponsors = useMemo(() => {
        const q = sponsorSearch.trim().toLowerCase();
        if (!showSponsorSearch || !q) return sponsors;
        return sponsors.filter((s) =>
            String(s.sponsor_name || '').toLowerCase().includes(q)
            || String(s.sponsor_id || '').toLowerCase().includes(q)
        );
    }, [sponsors, sponsorSearch, showSponsorSearch]);

    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user, userPermissions } = useContext(AuthContext);
    const [hasPermission, setHasPermission] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);

    const [showBackButton, setShowBackButton] = useState(false);

    // Superuser sponsor management state
    const isSuperuser = user?.is_superuser;
    const [linkedSponsorIds, setLinkedSponsorIds] = useState([]);
    const [updatingSponsorId, setUpdatingSponsorId] = useState(null);

    // Superuser sponsor creation state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        sponsor_id: '',
        sponsor_name: '',
        sponsor_email: '',
        sponsor_phone_number: '',
        sponsor_contact_first_name: '',
        sponsor_contact_last_name: '',
        email_domains: ''
    });
    const [creatingSponsor, setCreatingSponsor] = useState(false);
    const [createError, setCreateError] = useState(null);

    // Superuser sponsor edit state
    const [editingSponsor, setEditingSponsor] = useState(null);  // full sponsor record being edited
    const [editForm, setEditForm] = useState(null);
    const [savingSponsorEdit, setSavingSponsorEdit] = useState(false);
    const [editError, setEditError] = useState(null);

    useEffect(() => {
        const fromMainMenu = location.state?.from === 'mainMenu';
        setShowBackButton(fromMainMenu);
    }, [location]);

    // Check sponsor selection permission using bulk permissions from AuthContext.
    // This avoids multiple /api/user/permissions/<perm>/ calls which can trigger rate limiting.
    useEffect(() => {
        if (!isAuthenticated) {
            setHasPermission(false);
            setAccessDenied(true);
            setLoading(false);
            // Redirect to login after a short delay to allow seeing the access denied message
            setTimeout(() => navigate('/login', { state: { from: location.pathname } }), 3000);
            return;
        }

        // userPermissions may load slightly after isAuthenticated flips true.
        // Wait for permissions to actually load (not just an empty object)
        if (!userPermissions || Object.keys(userPermissions).length === 0) {
            setHasPermission(null);
            return;
        }

        // Internal users with zero assigned sponsors are allowed in so the
        // "No Sponsors Assigned" empty state can render, matching mobile.
        const sponsorCount = userPermissions.sponsor_count || 0;
        const canSelectSponsor = userPermissions.can_select_sponsor === true ||
            (user?.is_internal === true && sponsorCount === 0);
        setHasPermission(canSelectSponsor);

        if (!canSelectSponsor) {
            setAccessDenied(true);
            setLoading(false);
            // Redirect to home/dashboard after a short delay
            setTimeout(() => navigate('/'), 3000);
        }
    }, [isAuthenticated, navigate, location.pathname, userPermissions, user]);

    useEffect(() => {
        const fetchSponsors = async () => {
            if (!hasPermission) {
                return;
            }

            setLoading(true);
            setFetchError(false);
            let retryScheduled = false;
            try {
                const response = await sponsorService.getSponsors();

                // Handle auto-bypass response for single-sponsor users
                if (response.auto_selected) {
                    console.log('Single sponsor auto-selected, redirecting to studies');
                    setAutoSelecting(true);
                    // Update auth context if tokens are provided
                    if (response.tokens?.access_token) {
                        localStorage.setItem('authToken', response.tokens.access_token);
                        if (response.tokens.refresh_token) {
                            localStorage.setItem('refreshToken', response.tokens.refresh_token);
                        }
                    }
                    
                    // Store selected sponsor info
                    if (response.sponsor_id && response.sponsor_name) {
                        localStorage.setItem('selectedSponsor', JSON.stringify({
                            id: response.sponsor_id,
                            name: response.sponsor_name
                        }));
                        window.dispatchEvent(new Event('storage'));
                    }

                    // Replace this screen in history so it never appears in back navigation
                    navigate('/studies', { replace: true });
                    return;
                }
                
                // Normal case - set sponsors list (only sponsors this user is assigned to)
                setSponsors(response);

                // For superusers, also:
                // 1) Load which sponsors are currently linked via M2M (for badges/buttons)
                // 2) Load ALL sponsors in the system for the management panel
                if (isSuperuser) {
                    try {
                        const links = await sponsorService.getMySponsorLinks();
                        if (Array.isArray(links?.sponsor_ids)) {
                            setLinkedSponsorIds(links.sponsor_ids);
                        }
                    } catch (linkError) {
                        console.error('Error loading current sponsor links:', linkError);
                    }

                    try {
                        const all = await sponsorService.getAllSponsors();
                        setAllSponsors(Array.isArray(all) ? all : []);
                    } catch (allError) {
                        console.error('Error loading all sponsors for superuser panel:', allError);
                    }
                }
            } catch (error) {
                if (error?.response?.status === 429) {
                    // If rate limited, wait 2 seconds and try again
                    retryScheduled = true;
                    setTimeout(async () => {
                        try {
                            const response = await sponsorService.getSponsors();

                            // Handle auto-bypass on retry too
                            if (response.auto_selected) {
                                console.log('Single sponsor auto-selected on retry, redirecting to studies');
                                setAutoSelecting(true);
                                if (response.tokens?.access_token) {
                                    localStorage.setItem('authToken', response.tokens.access_token);
                                    if (response.tokens.refresh_token) {
                                        localStorage.setItem('refreshToken', response.tokens.refresh_token);
                                    }
                                }
                                if (response.sponsor_id && response.sponsor_name) {
                                    localStorage.setItem('selectedSponsor', JSON.stringify({
                                        id: response.sponsor_id,
                                        name: response.sponsor_name
                                    }));
                                    window.dispatchEvent(new Event('storage'));
                                }
                                navigate('/studies', { replace: true });
                                return;
                            }

                            setSponsors(response);
                        } catch (retryError) {
                            console.error('Error retrying sponsor fetch:', retryError);
                            setFetchError(true);
                        } finally {
                            setLoading(false);
                        }
                    }, 2000);
                } else {
                    console.error('Error fetching sponsors:', error);
                    setFetchError(true);
                }
            } finally {
                // Keep the loading state through a scheduled 429 retry so the
                // skeleton stays up instead of flashing the empty state
                if (!retryScheduled) {
                    setLoading(false);
                }
            }
        };

        fetchSponsors();
    }, [hasPermission, navigate, isSuperuser, retryToken]);

    const handleToggleSponsorLink = async (sponsor) => {
        if (!isSuperuser) return;
        const sponsorId = sponsor.sponsor_id;
        const currentlyLinked = linkedSponsorIds.includes(sponsorId);
        setUpdatingSponsorId(sponsorId);
        try {
            if (currentlyLinked) {
                await sponsorService.removeSponsorFromUser(sponsorId);
                setLinkedSponsorIds((prev) => prev.filter((id) => id !== sponsorId));
                // Optimistically remove from current assigned sponsors list so UI updates immediately
                setSponsors((prev) => prev.filter((s) => s.sponsor_id !== sponsorId));
            } else {
                await sponsorService.addSponsorToUser(sponsorId);
                setLinkedSponsorIds((prev) => prev.includes(sponsorId) ? prev : [...prev, sponsorId]);
            }

            // After changing links, refresh the assigned sponsors list so the
            // main selector grid reflects the new associations immediately.
            try {
                const refreshed = await sponsorService.getSponsors();
                if (!refreshed.auto_selected) {
                    setSponsors(refreshed);
                }
            } catch (refreshError) {
                console.error('Error refreshing assigned sponsors after link change:', refreshError);
            }
        } catch (error) {
            console.error('Error toggling sponsor link:', error);
        } finally {
            setUpdatingSponsorId(null);
        }
    };

    const handleSponsorSelect = async (sponsor) => {
        setSelectedSponsor(sponsor);
        try {
            console.log('Selecting sponsor:', sponsor.sponsor_id);
            const response = await sponsorService.selectSponsor(sponsor.sponsor_id);
            console.log('Sponsor selection response:', response);

            // Store selected sponsor with full details
            localStorage.setItem('selectedSponsor', JSON.stringify({
                id: sponsor.sponsor_id,
                name: sponsor.sponsor_name
            }));
            window.dispatchEvent(new Event('storage'));

            // Navigate directly to studies
            navigate('/studies');
        } catch (error) {
            console.error('Error selecting sponsor:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            setSelectedSponsor(null);
        }
    };

    const handleCreateSponsor = async () => {
        setCreatingSponsor(true);
        setCreateError(null);
        try {
            // Convert comma-separated email_domains string into a list for backend
            const emailDomainsList = (createForm.email_domains || '')
                .split(',')
                .map((d) => d.trim())
                .filter((d) => d.length > 0);

            const payload = {
                ...createForm,
                sponsor_is_active: true,
                sponsor_active_users: 0,
                email_domains: emailDomainsList,
            };
            const newSponsor = await sponsorService.createSponsor(payload);
            // New sponsor should now be linked to the creating user (backend logic),
            // so add it to the assigned sponsors list
            setSponsors((prev) => [...prev, newSponsor]);

            // Also refresh the full sponsor list for the Superuser panel
            try {
                const all = await sponsorService.getAllSponsors();
                setAllSponsors(Array.isArray(all) ? all : []);
            } catch (allError) {
                console.error('Error refreshing all sponsors after create:', allError);
            }

            setShowCreateModal(false);

            setCreateForm({
                sponsor_id: '',
                sponsor_name: '',
                sponsor_email: '',
                sponsor_phone_number: '',
                sponsor_contact_first_name: '',
                sponsor_contact_last_name: '',
                email_domains: ''
            });
        } catch (error) {
            setCreateError(error?.response?.data || 'Failed to create sponsor');
        } finally {
            setCreatingSponsor(false);
        }
    };

    const handleOpenEditModal = async (sponsor, event) => {
        event.stopPropagation();
        setEditError(null);
        try {
            // Fetch the full record; the selector list payload is a slim shape
            const full = await sponsorService.getSponsor(sponsor.sponsor_id);
            setEditForm({
                sponsor_name: full.sponsor_name || '',
                sponsor_email: full.sponsor_email || '',
                sponsor_phone_number: full.sponsor_phone_number || '',
                sponsor_contact_first_name: full.sponsor_contact_first_name || '',
                sponsor_contact_last_name: full.sponsor_contact_last_name || '',
                email_domains: (full.email_domains || []).join(', '),
                sponsor_is_active: full.sponsor_is_active !== false,
                mfa_required_for_all_users: !!full.mfa_required_for_all_users,
                offline_cutoff_hours: full.offline_cutoff_hours ?? '',
                enforce_approval: full.enforce_approval,  // true | false | null (inherit)
            });
            setEditingSponsor(full);
        } catch (error) {
            console.error('Error loading sponsor for edit:', error);
            toast.error('Failed to load sponsor details');
        }
    };

    const handleSaveSponsorEdit = async () => {
        if (!editingSponsor || !editForm) return;
        setSavingSponsorEdit(true);
        setEditError(null);
        try {
            const emailDomainsList = (editForm.email_domains || '')
                .split(',')
                .map((d) => d.trim())
                .filter((d) => d.length > 0);

            const payload = {
                sponsor_name: editForm.sponsor_name,
                sponsor_email: editForm.sponsor_email,
                sponsor_phone_number: editForm.sponsor_phone_number,
                sponsor_contact_first_name: editForm.sponsor_contact_first_name,
                sponsor_contact_last_name: editForm.sponsor_contact_last_name,
                sponsor_is_active: editForm.sponsor_is_active,
                mfa_required_for_all_users: editForm.mfa_required_for_all_users,
                offline_cutoff_hours: editForm.offline_cutoff_hours === ''
                    ? null
                    : parseInt(editForm.offline_cutoff_hours, 10),
                email_domains: emailDomainsList,
            };
            const updated = await sponsorService.updateSponsor(editingSponsor.sponsor_id, payload);

            // Approval enforcement changes go through the dedicated audited endpoint
            if (editForm.enforce_approval !== editingSponsor.enforce_approval) {
                await sponsorService.setSponsorApprovalEnforcement(
                    editingSponsor.sponsor_id,
                    editForm.enforce_approval
                );
                updated.enforce_approval = editForm.enforce_approval;
            }

            const mergeUpdated = (list) => list.map((s) => (
                s.sponsor_id === updated.sponsor_id ? { ...s, ...updated } : s
            ));
            setSponsors(mergeUpdated);
            setAllSponsors(mergeUpdated);

            toast.success('Sponsor updated');
            setEditingSponsor(null);
            setEditForm(null);
        } catch (error) {
            console.error('Error updating sponsor:', error);
            setEditError(error?.response?.data || 'Failed to update sponsor');
        } finally {
            setSavingSponsorEdit(false);
        }
    };

    // Redirect users without permission
    useEffect(() => {
        if (hasPermission === false) {
            console.log('User does not have sponsor selection permission, redirecting to /studies');
            navigate('/studies', { 
                state: { 
                    accessDenied: true, 
                    message: 'You do not have permission to access the Sponsor Selection.' 
                } 
            });
        }
    }, [hasPermission, navigate]);
    
    // Skeleton cards mirroring the sponsor card layout, shown while loading
    const skeletonGrid = (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
                <div
                    key={`sponsor-skeleton-${i}`}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white animate-pulse dark:border-gray-700 dark:bg-gray-800"
                >
                    <div className="p-6">
                        <div className="mb-4 h-10 w-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
                        <div className="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-700 mb-6" />
                        <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700 mb-2" />
                        <div className="h-3 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                    <div className="border-t border-gray-100 bg-gray-50/70 px-6 py-3 dark:border-gray-700 dark:bg-gray-900/30">
                        <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                </div>
            ))}
        </div>
    );

    // Single sponsor is being auto-selected; shown until the redirect lands
    if (autoSelecting) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
                <Loader2 className="mb-5 h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                <h2 className="mb-1.5 text-lg font-semibold text-gray-900 dark:text-gray-100">Preparing your workspace</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Taking you to your sponsor's studies...</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="mx-auto w-full max-w-5xl py-2 sm:py-6">
                <div className="card w-full p-6 sm:p-8">
                    <div className="animate-pulse mb-8">
                        <div className="h-8 w-64 rounded bg-gray-200 dark:bg-gray-700 mb-4" />
                        <div className="h-4 w-96 max-w-full rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                    {skeletonGrid}
                </div>
            </div>
        );
    }

    if (hasPermission === false) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="card w-full max-w-md p-8 text-center">
                    <div className="mb-4 flex justify-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 dark:bg-danger-500/10">
                            <AlertTriangle className="h-6 w-6 text-danger-600 dark:text-danger-400" />
                        </div>
                    </div>
                    <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Access Denied</h2>
                    <p className="mb-5 text-sm text-gray-600 dark:text-gray-400">You do not have permission to access Sponsor Selection.</p>
                    <button
                        onClick={() => navigate('/studies')}
                        className="btn btn-primary"
                    >
                        Return to Studies
                    </button>
                </div>
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.2, ease: 'easeOut' },
        },
    };

    const itemVariants = {
        hidden: { y: 8, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.25, ease: 'easeOut' },
        },
    };

    return (
        <>
            <div className="mx-auto w-full max-w-5xl py-2 sm:py-6">
                <div className="card w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="p-6 sm:p-8"
                    >
                        {accessDenied ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 dark:bg-danger-500/10">
                                    <AlertOctagon className="h-6 w-6 text-danger-600 dark:text-danger-400" />
                                </div>
                                <h1 className="mb-2 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">Access Denied</h1>
                                <p className="mb-6 max-w-md text-center text-sm text-gray-600 dark:text-gray-400">
                                    You do not have permission to access Sponsor Selection.
                                </p>
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: '100%' }}
                                    transition={{ duration: 3 }}
                                    className="mb-6 h-1 w-64 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                                >
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 3 }}
                                        className="h-full bg-primary-500"
                                    />
                                </motion.div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting you...</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-8 border-b border-gray-200/80 pb-6 dark:border-gray-700/80">
                                    <h1 className="page-title">Select a Sponsor</h1>
                                    <p className="page-subtitle">Choose a sponsor to access their studies and data.</p>
                                </div>

                                <div className="mb-6">
                                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <Building2 className="h-[18px] w-[18px] text-primary-600 dark:text-primary-400" />
                                            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Available Sponsors</h2>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {isSuperuser && (
                                                <button
                                                    onClick={() => setShowCreateModal(true)}
                                                    className="btn btn-primary btn-sm"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                    Create Sponsor
                                                </button>
                                            )}
                                            {showBackButton && (
                                                <button
                                                    onClick={() => navigate('/')}
                                                    className="btn btn-ghost btn-sm"
                                                >
                                                    Back
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {showSponsorSearch && !loading && !fetchError && (
                                        <div className="relative mb-5">
                                            <input
                                                type="text"
                                                placeholder="Search by sponsor name or ID..."
                                                value={sponsorSearch}
                                                onChange={(e) => setSponsorSearch(e.target.value)}
                                                className="input pl-10 pr-4"
                                            />
                                            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                                        </div>
                                    )}
                                    {loading ? (
                                        skeletonGrid
                                    ) : fetchError ? (
                                        // Error state with retry when the sponsor list fails to load
                                        <motion.div
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            className="flex flex-col items-center justify-center px-8 py-14"
                                        >
                                            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 dark:bg-danger-500/10">
                                                <AlertTriangle className="h-6 w-6 text-danger-600 dark:text-danger-400" />
                                            </div>
                                            <h3 className="mb-2 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                                                Unable to Load Sponsors
                                            </h3>
                                            <p className="mb-6 max-w-md text-center text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                                                Something went wrong while loading your sponsors. Check your connection and try again.
                                            </p>
                                            <button
                                                onClick={() => setRetryToken((t) => t + 1)}
                                                className="btn btn-primary"
                                            >
                                                Retry
                                            </button>
                                        </motion.div>
                                    ) : sponsors.length === 0 ? (
                                        // Empty state when no sponsors are assigned
                                        <motion.div
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            className="flex flex-col items-center justify-center px-8 py-14"
                                        >
                                            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-warning-50 dark:bg-warning-500/10">
                                                <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                                            </div>
                                            <h3 className="mb-2 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                                                No Sponsors Assigned
                                            </h3>
                                            <p className="mb-6 max-w-md text-center text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                                                You haven't been assigned to any sponsors yet. Please contact the dev team to request sponsor access.
                                            </p>
                                            <div className="max-w-md rounded-lg border border-gray-200/70 bg-gray-50 px-4 py-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
                                                <div className="flex items-start gap-2.5">
                                                    <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                                                    <div>
                                                        <h4 className="mb-0.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                            Need Access?
                                                        </h4>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                            Contact the dev team to be assigned to one or more sponsors.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            {showBackButton && (
                                                <button
                                                    onClick={() => navigate('/')}
                                                    className="btn btn-primary mt-6"
                                                >
                                                    Back to Dashboard
                                                </button>
                                            )}
                                        </motion.div>
                                    ) : visibleSponsors.length === 0 ? (
                                        <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No sponsors match your search.
                                        </div>
                                    ) : (
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                                        >
                                            <AnimatePresence>
                                                {visibleSponsors.map((sponsor) => (
                                                    <motion.div
                                                        key={sponsor.sponsor_id}
                                                        variants={itemVariants}
                                                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                                                        className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card transition-colors duration-150 hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-700 ${
                                                            selectedSponsor?.sponsor_id === sponsor.sponsor_id ? 'ring-2 ring-primary-500' : ''
                                                        }`}
                                                        onClick={() => handleSponsorSelect(sponsor)}
                                                    >
                                                        <div className="flex flex-1 flex-col p-5">
                                                            <div className="mb-4 flex items-center justify-between">
                                                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-900/30 dark:text-primary-400 dark:ring-primary-800/40">
                                                                    <Building2 className="h-5 w-5" />
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {isSuperuser && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => handleOpenEditModal(sponsor, e)}
                                                                            title="Edit sponsor"
                                                                            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:text-gray-500 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                                                                        >
                                                                            <Pencil className="h-4 w-4" />
                                                                        </button>
                                                                    )}
                                                                    {selectedSponsor?.sponsor_id === sponsor.sponsor_id && (
                                                                        <CheckCircle className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            <h2 className="mb-1 text-lg font-semibold text-gray-900 transition-colors duration-150 group-hover:text-primary-700 dark:text-gray-100 dark:group-hover:text-primary-400">
                                                                {sponsor.sponsor_name}
                                                            </h2>
                                                            
                                                            <div className="mt-auto space-y-1.5 pt-3">
                                                                <div className="flex min-w-0 items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <Mail className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
                                                                    <span className="truncate">{sponsor.sponsor_email}</span>
                                                                </div>
                                                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <span>ID: {sponsor.sponsor_id}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="mt-auto flex items-center justify-between border-t border-gray-100 bg-gray-50/70 px-5 py-3 transition-colors group-hover:bg-primary-50/60 dark:border-gray-700 dark:bg-gray-900/30 dark:group-hover:bg-primary-900/20">
                                                            <span className="text-sm font-medium text-primary-700 dark:text-primary-400">View details</span>
                                                            <ArrowUpRight className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </motion.div>
                                    )}
                                    {showSponsorSearch && !loading && !fetchError && (
                                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-right">
                                            {visibleSponsors.length} of {sponsors.length} sponsors
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
            {isSuperuser && allSponsors.length > 0 && (
                <div className="mx-auto mb-8 mt-2 w-full max-w-5xl">

                    <div className="card p-6">
                        <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Superuser Sponsor Access</h2>
                        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                            Quickly add or remove sponsors from your account. This controls which sponsors you can switch into from the selector.
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {allSponsors.map((sponsor) => {

                                const linked = linkedSponsorIds.includes(sponsor.sponsor_id);
                                const isUpdating = updatingSponsorId === sponsor.sponsor_id;
                                return (
                                    <div
                                        key={`manage-${sponsor.sponsor_id}`}
                                        className="flex flex-col justify-between rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/30"
                                    >
                                        <div>
                                            <div className="mb-1 flex items-center justify-between gap-2">
                                                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {sponsor.sponsor_name}
                                                </span>
                                                {linked && (
                                                    <span className="badge badge-success flex-shrink-0">
                                                        Linked
                                                    </span>
                                                )}
                                            </div>
                                            <p className="truncate text-xs text-gray-500 dark:text-gray-400">ID: {sponsor.sponsor_id}</p>
                                        </div>
                                        <div className="mt-3 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => handleToggleSponsorLink(sponsor)}
                                                disabled={isUpdating}
                                                className={`btn btn-sm ${
                                                    linked
                                                        ? 'btn-danger-soft border border-danger-500/25 bg-white dark:bg-transparent'
                                                        : 'btn-secondary'
                                                } ${isUpdating ? 'opacity-60 cursor-wait' : ''}`}
                                            >
                                                {isUpdating
                                                    ? 'Updating...'
                                                    : linked
                                                        ? 'Remove from my sponsors'
                                                        : 'Add to my sponsors'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {isSuperuser && editingSponsor && editForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
                    <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 animate-slide-up dark:bg-gray-800 dark:ring-white/10">
                        <div className="px-6 pb-2 pt-5">
                            <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
                                <Pencil className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                                Edit Sponsor
                            </h2>
                            <p className="mt-0.5 pl-[30px] text-sm text-gray-500 dark:text-gray-400">ID: {editingSponsor.sponsor_id}</p>
                        </div>
                        <div className="px-6 pb-5 pt-3">
                        {editError && (
                            <div className="mb-4 rounded-lg border border-danger-500/25 bg-danger-50 px-3.5 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400">
                                {typeof editError === 'string' ? editError : JSON.stringify(editError)}
                            </div>
                        )}
                        <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                            <div>
                                <label className="label">Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={editForm.sponsor_name}
                                    onChange={(e) => setEditForm({ ...editForm, sponsor_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label">Email</label>
                                <input
                                    type="email"
                                    className="input"
                                    value={editForm.sponsor_email}
                                    onChange={(e) => setEditForm({ ...editForm, sponsor_email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label">Phone</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={editForm.sponsor_phone_number}
                                    onChange={(e) => setEditForm({ ...editForm, sponsor_phone_number: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label">Contact First Name</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={editForm.sponsor_contact_first_name}
                                        onChange={(e) => setEditForm({ ...editForm, sponsor_contact_first_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="label">Contact Last Name</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={editForm.sponsor_contact_last_name}
                                        onChange={(e) => setEditForm({ ...editForm, sponsor_contact_last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label">Email Domains (comma-separated)</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={editForm.email_domains}
                                    onChange={(e) => setEditForm({ ...editForm, email_domains: e.target.value })}
                                />
                            </div>
                            <label className="option-row cursor-pointer items-center">
                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Sponsor is active</span>
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    checked={editForm.sponsor_is_active}
                                    onChange={(e) => setEditForm({ ...editForm, sponsor_is_active: e.target.checked })}
                                />
                            </label>

                            <div className="mt-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">Global Configuration</h3>
                                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                                    These settings apply across all of this sponsor's studies, sites, and participants.
                                </p>
                                <label className="option-row cursor-pointer items-center">
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        Require MFA for all studies
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                                            Staff on every study under this sponsor must set up MFA
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        checked={editForm.mfa_required_for_all_users}
                                        onChange={(e) => setEditForm({ ...editForm, mfa_required_for_all_users: e.target.checked })}
                                    />
                                </label>
                                <div className="mt-4">
                                    <label className="label">Approval Enforcement</label>
                                    <select
                                        className="input"
                                        value={editForm.enforce_approval === null ? 'inherit' : editForm.enforce_approval ? 'on' : 'off'}
                                        onChange={(e) => setEditForm({
                                            ...editForm,
                                            enforce_approval: e.target.value === 'inherit' ? null : e.target.value === 'on',
                                        })}
                                    >
                                        <option value="inherit">Inherit environment default</option>
                                        <option value="on">Enforced</option>
                                        <option value="off">Disabled</option>
                                    </select>
                                    <p className="hint">
                                        Require the review/approval workflow before publishing assessments and studies.
                                    </p>
                                </div>
                                <div className="mt-4">
                                    <label className="label">Offline Cutoff (Hours)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input"
                                        value={editForm.offline_cutoff_hours}
                                        onChange={(e) => setEditForm({ ...editForm, offline_cutoff_hours: e.target.value })}
                                    />
                                    <p className="hint">
                                        Max hours a device may stay offline before new offline answers lock. Blank inherits the environment default; 0 disables the cutoff.
                                    </p>
                                </div>
                            </div>
                        </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setEditingSponsor(null);
                                    setEditForm(null);
                                    setEditError(null);
                                }}
                                disabled={savingSponsorEdit}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSaveSponsorEdit}
                                disabled={savingSponsorEdit}
                            >
                                {savingSponsorEdit ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isSuperuser && showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
                    <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 animate-slide-up dark:bg-gray-800 dark:ring-white/10">
                        <div className="px-6 pb-2 pt-5">
                            <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
                                <Building2 className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                                Create Sponsor
                            </h2>
                        </div>
                        <div className="px-6 pb-5 pt-3">
                        {createError && (
                            <div className="mb-4 rounded-lg border border-danger-500/25 bg-danger-50 px-3.5 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400">
                                {typeof createError === 'string' ? createError : JSON.stringify(createError)}
                            </div>
                        )}
                        <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                            <div>
                                <label className="label">Sponsor ID</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={createForm.sponsor_id}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_id: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label">Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={createForm.sponsor_name}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label">Email</label>
                                <input
                                    type="email"
                                    className="input"
                                    value={createForm.sponsor_email}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label">Phone</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={createForm.sponsor_phone_number}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_phone_number: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label">Contact First Name</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={createForm.sponsor_contact_first_name}
                                        onChange={(e) => setCreateForm({ ...createForm, sponsor_contact_first_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="label">Contact Last Name</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={createForm.sponsor_contact_last_name}
                                        onChange={(e) => setCreateForm({ ...createForm, sponsor_contact_last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label">Email Domains (comma-separated)</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={createForm.email_domains}
                                    onChange={(e) => setCreateForm({ ...createForm, email_domains: e.target.value })}
                                />
                            </div>
                        </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setCreateError(null);
                                }}
                                disabled={creatingSponsor}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleCreateSponsor}
                                disabled={creatingSponsor}
                            >
                                {creatingSponsor ? 'Creating...' : 'Create Sponsor'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SponsorSelector;